
import { Component, Mixins, Prop } from "vue-property-decorator";
import ValidatedFormMixin from '@/common/mixins/validated-form.mixin';
import enumUtils from '@/utils/enum.util';
import OfficeProfileMixin from "../../office-profiles.mixin";
import AuthMixin from "@/common/mixins/auth.mixin";
import { mapState } from "vuex";
import { OfficeDefaultRateFormsDto, OfficeFormDto, OfficeHourFormDto, OfficePayrollFormDto, OfficeUserDto, OfficeDataDto, OfficeUserFormDto } from "@/types/office.type";
import { EmployeeType, PayrollType } from "@/types/enums.type";
import { StatusNames } from "@/common/constants";

@Component({
  computed: {
    ...mapState("OfficeProfile", ["infoForm", "rateForm", "payrollForm", "hourForm", "officeUsers", "officeData"])
  }
})
export default class OfficePayrollEditComponent extends Mixins(OfficeProfileMixin, ValidatedFormMixin, AuthMixin) {
  @Prop() activeTab!: number;
  
  infoForm!: OfficeFormDto;
  rateForm!: OfficeDefaultRateFormsDto;
  payrollForm!: OfficePayrollFormDto;
  hourForm!: OfficeHourFormDto;
  officeUsers!: OfficeUserDto[];
  officeData!: OfficeDataDto;

  maxDescLength = 200;

  employeeTypeOptions = enumUtils.toOptions(
    EmployeeType,
    [
      { text: 'W2', value: EmployeeType.WTwo},
      { text: '1099', value: EmployeeType.OneZeroNineNine }
    ]
  );

  payrollTypeOptions = enumUtils.toOptions(
    PayrollType,
    [
      { text: 'End of day (EOD) - Dental Temp will be paid at the end of their shift.', value: PayrollType.EndOfDay },
      { text: 'Weekly (wkly) - Payroll is processed once a week.', value: PayrollType.Weekly },
      { text: 'Biweekly (biwk) - Payroll is processed every other week.', value: PayrollType.Biweekly },
      { text: `Direct Deposit (DD) - Payroll is processed using direct deposit 2-7 days after shift. 
      Deposit times vary depending on the EFT transfer process.`, value: PayrollType.DirectDeposit }
    ]
  );

  PayrollType = PayrollType;

  mounted(): void {
    if (!this.selectedOffice) {
      return;
    }

    this.$store.commit("OfficeProfile/updateField", {
      path: "payrollForm",
      value: {
        id: this.selectedOffice.payroll?.id ?? 0,
        employeeType: this.selectedOffice.payroll?.employeeType ?? 0,
        type: this.selectedOffice.payroll?.type ?? 0,
        description: this.selectedOffice.payroll?.description ?? null,
        officeId: this.selectedOffice.id
      } as OfficePayrollFormDto 
    });
    
    this.$validator.reset();
  }

  async update(): Promise<void> {
    this.$validator.validateAll().then(async (isValid: boolean) => {
      if (isValid) {
        this.setLoading(true);

        if (this.payrollForm.type !== PayrollType.Other) {
          this.payrollForm.description = null;
        }

        await this.$store.dispatch("OfficeProfile/savePayroll", this.payrollForm);

        this.setLoading(false);
      }
    });
  }

  back(): void {
    this.$emit('update:activeTab', 4);
  }

  async finish(): Promise<void> {
    await this.$validator.validateAll().then(async (isValid: boolean) => {
      if (isValid) {
        this.setLoading(true);

        const officeId = await this.$store.dispatch("OfficeProfile/saveOffice", this.infoForm);
        
        for (var user of this.officeUsers) {
          await this.$store.dispatch("OfficeProfile/saveOfficeUser", {
            userId: user.userId,
            officeId: officeId,
            status: this.userInfo()?.sub === user.userId ? StatusNames.Enabled.getId(this.officeData.statuses) : user.officeUserStatus
          } as OfficeUserFormDto);
        }
        await this.$store.dispatch("OfficeProfile/saveOfficeHours", {...this.hourForm, officeId} as OfficeHourFormDto);

        const rateForms = { rates: [...this.rateForm.rates] } as OfficeDefaultRateFormsDto;
        rateForms.rates.forEach(rate => rate.officeId = officeId);

        await this.$store.dispatch("OfficeProfile/saveOfficeRates", this.rateForm);

        this.payrollForm.officeId = officeId;

        if (this.payrollForm.type !== PayrollType.Other) {
          this.payrollForm.description = null;
        }

        await this.$store.dispatch("OfficeProfile/savePayroll", this.payrollForm);

        this.setLoading(false);

        this.$router.push("/office-profiles");
      }
    });
  }
}
